import { Tooltip, Upload, message } from 'antd';
import { ToolbarIcon } from '../../ToolbarIcon';
import { useTranslate } from '@tolgee/react';
import { platformUploadImageAcceptMap } from 'services/config/platform';
import toLowerCase from 'utils/toLowerCase';
import { useMemoizedFn } from 'ahooks';
import { RcFile } from 'antd/lib/upload';
import {
  MsgAttachmentInfo,
  useMsgAttachmentList,
  useUploadAttachment,
} from './hooks/useUploadAttachment';
import { getAttachmentMaxCount, validateAttachmentFile } from './utils';
import { uploadClass } from '../styles';

interface AttachmentUploadActionProps {
  conversation: Conversation.Conversation;
}

export const AttachmentUploadActionComponent = (props: {
  channel: General.PlatformUpperCaseTunnel;
  attachmentList: Util.PartialPartial<MsgAttachmentInfo, 'storeId'>[];
  onAttachmentUpload: (file: RcFile) => any;
  children?: JSX.Element;
}) => {
  const { channel, attachmentList, onAttachmentUpload, children } = props;

  const { t } = useTranslate();
  const isJolect = channel === 'JOLECT';
  const maxCount = getAttachmentMaxCount(channel);

  const handleBeforeUpload = useMemoizedFn((file: RcFile, fileList: RcFile[]) => {
    if (fileList.length > maxCount) {
      message.warn(
        t('scene.chatWorkspace.messageEditor.upload.validation.maxAttachmentCount', {
          count: maxCount,
        }),
      );
      return Upload.LIST_IGNORE;
    }
    if (!validateAttachmentFile(channel, file, attachmentList.length, t)) {
      return Upload.LIST_IGNORE;
    }

    onAttachmentUpload(file);

    return Upload.LIST_IGNORE;
  });
  return (
    <Tooltip title={t('scene.chatWorkspace.messageEditor.upload.action.uploadFiles')}>
      <Upload
        className={uploadClass}
        name="file"
        accept={platformUploadImageAcceptMap[toLowerCase(channel)]
          .map((type) => `.${type}`)
          .join(',')}
        multiple={!isJolect}
        maxCount={maxCount}
        showUploadList={false}
        beforeUpload={handleBeforeUpload}
      >
        {children || <ToolbarIcon icon="attachment" />}
      </Upload>
    </Tooltip>
  );
};

export const AttachmentUploadAction = (props: AttachmentUploadActionProps) => {
  const {
    conversation: { conversationId, channel, storeId, region },
  } = props;
  const uploadAttachment = useUploadAttachment();
  const msgAttachmentList = useMsgAttachmentList(conversationId);

  return (
    <AttachmentUploadActionComponent
      channel={channel}
      attachmentList={msgAttachmentList}
      onAttachmentUpload={(file) =>
        uploadAttachment(conversationId, storeId, region, file, {
          withMediaMetadata: channel === 'JOLECT',
        })
      }
    />
  );
};
